/**
 * Invoice domain contracts — Module 5 (TW-3450), the record TC bills a trip with.
 *
 * The lifecycle is **Draft → Issued → Paid**. A Draft is TC-internal: it is prefilled from the
 * accepted proposal's total, freely editable/deletable, and **never visible to the club** — the
 * club-invisibility is enforced in the database by a second RLS predicate (data-model §10.3), so
 * the rule holds even if the app layer has a bug. "Issue to club" flips it visible (`Issued`), and
 * "Mark as paid" stamps the payment (`Paid`). As elsewhere, the Zod schemas here are the single
 * source of truth for request validation (API + web), and the small lifecycle/visibility helpers are
 * pure so the web and server agree on which actions are available and what the club may see (web has
 * no test runner, so they are proven in the API vitest harness — mirrors the proposal maths).
 */
import { z } from "zod";
import { type DocumentKind, type InvoiceStatus } from "./constants";

// ── Lifecycle ───────────────────────────────────────────────────────────────

/**
 * Forward-only invoice state machine (mirrors the trip/proposal maps in TW-3437/3445): a Draft can
 * only be issued, an Issued invoice can only be marked paid, and Paid is terminal. The service walks
 * this map and writes an explicit-in-tx `status_change` audit row on each genuine transition.
 */
export const INVOICE_STATUS_TRANSITIONS: Record<InvoiceStatus, readonly InvoiceStatus[]> = {
  Draft: ["Issued"],
  Issued: ["Paid"],
  Paid: [],
};

/** Whether `from → to` is a permitted invoice transition. */
export function canTransitionInvoice(from: InvoiceStatus, to: InvoiceStatus): boolean {
  return INVOICE_STATUS_TRANSITIONS[from].includes(to);
}

/**
 * Whether a club user may ever see an invoice in this status. A Draft is TC-internal (hidden by the
 * `invoice_tenant_isolation` RLS policy); only Issued/Paid reach the club. The web uses this to drive
 * the eye / eye-off visibility tag; the database is the actual enforcement point.
 */
export function isInvoiceClubVisible(status: InvoiceStatus): boolean {
  return status !== "Draft";
}

// ── Money ─────────────────────────────────────────────────────────────────────

/** Round to whole cents, avoiding binary-float drift — the shape the Decimal(12,2) column stores. */
export function roundMoney(amount: number): number {
  return Math.round((amount + Number.EPSILON) * 100) / 100;
}

/**
 * Format a money value as it is **printed on the factura** (TW-3527) — Spanish (`es-ES`) convention:
 * "." thousands grouping, "," decimal, currency symbol after a space ("1.234,56 €"). `useGrouping:
 * "always"` forces grouping even on 4-digit amounts (CLDR's `es-ES` would otherwise leave "2118,60"
 * ungrouped, which reads wrong on an invoice). The factura is a fixed `es-ES` document regardless of
 * the viewer's language, so the locale is hard-wired; only the currency varies. One definition shared
 * by the web factura view and the PDF generator so printed amounts always match to the character.
 */
export function formatFacturaMoney(amount: number, currency = "EUR"): string {
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency,
    useGrouping: "always",
  }).format(amount);
}

/**
 * The amount a new Draft is prefilled with: the accepted proposal's total (rounded to cents), or 0
 * when the trip has no priced proposal yet (TC can still raise a Draft and type the figure in).
 */
export function prefillInvoiceAmount(proposalTotal: number | null | undefined): number {
  return proposalTotal != null && proposalTotal > 0 ? roundMoney(proposalTotal) : 0;
}

const safe = (v: number) => (Number.isFinite(v) ? v : 0);

// ── Payment terms (TRAV-2) ────────────────────────────────────────────────────
//
// The factura's due date ("Vencimiento") is not typed in by hand: the operator picks payment TERMS
// (days from the document date) from a fixed set while the invoice is a Draft, and the actual
// `due_date` is computed + stamped at ISSUE time as `issue_date + due_days` — so the printed due
// date can never contradict the chosen terms. The helpers are pure so the Draft editor's preview,
// the server's issue-time stamp and the tests share one implementation.

/** The selectable payment terms, in days from the issue date. */
export const INVOICE_DUE_DAYS = [0, 7, 14, 30, 60, 90] as const;
export type InvoiceDueDays = (typeof INVOICE_DUE_DAYS)[number];

/** The terms a fresh invoice defaults to (mirrors the column default). */
export const DEFAULT_INVOICE_DUE_DAYS: InvoiceDueDays = 30;

/** Whether a number is one of the permitted {@link INVOICE_DUE_DAYS} values. */
export function isInvoiceDueDays(n: number): n is InvoiceDueDays {
  return (INVOICE_DUE_DAYS as readonly number[]).includes(n);
}

/**
 * The due date implied by an issue date + payment terms: `issueDate + dueDays`, both ISO
 * `YYYY-MM-DD` (calendar arithmetic in UTC — no time/tz, matching the `@db.Date` columns). Used by
 * the web preview on a Draft and by the server when stamping `due_date` at issue.
 */
export function computeInvoiceDueDate(issueDateIso: string, dueDays: number): string {
  const d = new Date(`${issueDateIso.slice(0, 10)}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + Math.trunc(safe(dueDays)));
  return d.toISOString().slice(0, 10);
}

// ── VAT line items (TW-3527) ──────────────────────────────────────────────────
//
// The structured factura itemises a trip into VAT-bearing line items. Each line carries its own
// rate from the standard Spanish set; this is plain itemised IVA (NOT the travel-agency REBU margin
// regime — confirmed with the client). The per-line maths and the per-rate VAT summary are pure +
// deterministic so the web view, the PDF generator and the server agree on one implementation (web
// has no test runner — proven in the API vitest harness). They mirror the supplied design's
// `lineMath`/tax-summary exactly: round to cents per line, then sum.

/** The VAT rates a line may carry (Spanish IVA): exempt, reduced, standard. */
export const VAT_RATES = [0, 10, 21] as const;
export type VatRate = (typeof VAT_RATES)[number];

/** The rate a freshly added line defaults to (the reduced travel rate). */
export const DEFAULT_VAT_RATE: VatRate = 10;

/** Whether a number is one of the permitted {@link VAT_RATES}. */
export function isVatRate(n: number): n is VatRate {
  return (VAT_RATES as readonly number[]).includes(n);
}

/** The economics needed to price one invoice line — a subset shared by the editor state and the DTO. */
export interface InvoiceLineMath {
  qty: number;
  /** Unit price, ex-VAT. */
  unitPrice: number;
  /** The line's VAT rate as a percentage (0 / 10 / 21). */
  vatRate: number;
}

/** A line's computed money: ex-VAT base (Sin IVA), the VAT amount (IVA) and the inc-VAT total (con IVA). */
export interface InvoiceLineTotals {
  /** Sin IVA — unit price × qty, rounded to cents. */
  ex: number;
  /** IVA — ex × rate%, rounded to cents. */
  iva: number;
  /** con IVA — ex + iva. */
  inc: number;
}

/** Per-line maths: base ex-VAT, the VAT amount and the inc-VAT total (rounded to cents per line). */
export function invoiceLineMath(line: InvoiceLineMath): InvoiceLineTotals {
  const ex = roundMoney(safe(line.unitPrice) * safe(line.qty));
  const iva = roundMoney((ex * safe(line.vatRate)) / 100);
  return { ex, iva, inc: roundMoney(ex + iva) };
}

/** One row of the per-rate VAT summary table — the base and VAT summed across all lines at that rate. */
export interface InvoiceVatRateRow {
  rate: VatRate;
  /** Σ ex-VAT base for this rate (Base Imponible). */
  base: number;
  /** Σ VAT for this rate. */
  iva: number;
}

/** The folded invoice totals: a per-rate VAT summary plus the document subtotal, VAT and grand total. */
export interface InvoiceTotals {
  /** One row per standard {@link VAT_RATES} value (always present, even at 0 — matches the design). */
  rates: InvoiceVatRateRow[];
  /** Σ ex-VAT base across every line (Base Imponible total). */
  subtotal: number;
  /** Σ VAT across every line. */
  vatTotal: number;
  /** Σ inc-VAT across every line (Importe total). */
  grandTotal: number;
}

/**
 * Fold a set of lines into the per-rate VAT summary + the document totals (mirrors the design's
 * tax-summary accumulation). A row is emitted for every standard rate, even one with no lines, so the
 * summary table is stable; rounding is per line then summed, so it ties out with the printed lines.
 */
export function summarizeInvoiceVat(lines: readonly InvoiceLineMath[]): InvoiceTotals {
  const byRate = new Map<VatRate, { base: number; iva: number }>();
  for (const r of VAT_RATES) byRate.set(r, { base: 0, iva: 0 });
  let subtotal = 0;
  let vatTotal = 0;
  let grandTotal = 0;
  for (const line of lines) {
    const m = invoiceLineMath(line);
    if (isVatRate(line.vatRate)) {
      const bucket = byRate.get(line.vatRate)!;
      bucket.base = roundMoney(bucket.base + m.ex);
      bucket.iva = roundMoney(bucket.iva + m.iva);
    }
    subtotal = roundMoney(subtotal + m.ex);
    vatTotal = roundMoney(vatTotal + m.iva);
    grandTotal = roundMoney(grandTotal + m.inc);
  }
  const rates = VAT_RATES.map((rate) => ({ rate, base: byRate.get(rate)!.base, iva: byRate.get(rate)!.iva }));
  return { rates, subtotal, vatTotal, grandTotal };
}

// ── Invoice numbering (TW-3527) ─────────────────────────────────────────────────
//
// TC's running invoice sequence is configurable in Back Office settings (replacing the old
// year-based `INV-YYYY-NNNN` allocator): a number is `prefix + zero-padded(sequence) + suffix`. The
// operator can edit the next sequence value to align with invoices issued outside this system. The
// formatter is pure so the settings preview, the issue-time allocation and the tests share it.

/** The configurable numbering scheme — a running sequence wrapped in a prefix/suffix, zero-padded. */
export interface InvoiceNumbering {
  prefix: string;
  suffix: string;
  /** The sequence value the next issued invoice will use. */
  nextNumber: number;
  /** Minimum digit width; the sequence is left-padded with zeros to at least this many digits. */
  minDigits: number;
}

/**
 * Format an invoice number from the numbering scheme and a sequence value (defaults to `nextNumber`)
 * — `prefix + pad(seq, minDigits) + suffix`, exactly the design's `invNumber`.
 */
export function formatInvoiceNumber(numbering: InvoiceNumbering, seq: number = numbering.nextNumber): string {
  const body = String(Math.max(0, Math.trunc(safe(seq)))).padStart(Math.max(0, numbering.minDigits | 0), "0");
  return `${numbering.prefix ?? ""}${body}${numbering.suffix ?? ""}`;
}

// ── Validation ──────────────────────────────────────────────────────────────

const amountSchema = z
  .number()
  .nonnegative()
  .max(99_999_999.99, "Amount is too large");

const currencySchema = z
  .string()
  .trim()
  .length(3, "Currency must be a 3-letter code")
  .transform((c) => c.toUpperCase());

/**
 * Create a Draft invoice on a trip. Both fields are optional: omit `amount` and the server prefills
 * it from the accepted proposal's total; omit `currency` and it defaults to the proposal's (EUR).
 */
export const createInvoiceSchema = z.object({
  amount: amountSchema.optional(),
  currency: currencySchema.optional(),
});
export type CreateInvoiceInput = z.infer<typeof createInvoiceSchema>;

/** Edit a Draft invoice (only Drafts are editable). Both fields required — the form sends both. */
export const updateInvoiceSchema = z.object({
  amount: amountSchema,
  currency: currencySchema,
});
export type UpdateInvoiceInput = z.infer<typeof updateInvoiceSchema>;

const vatRateSchema = z
  .number()
  .refine(isVatRate, { message: "VAT rate must be 0, 10 or 21" });

/**
 * One factura line as submitted by the editor (TW-3527). A line carries an optional display
 * group/section (headers derived on value change, no orphan header rows — mirrors the proposal
 * builder), a free-text `lineDate` (so ranges like "05-07/06/2026" are supported, never parsed), a
 * `description`, the quantity, the ex-VAT unit price and the per-line VAT rate. The ex/IVA/inc totals
 * and the VAT summary are derived (never sent), so only these source fields are validated.
 */
export const invoiceLineInputSchema = z.object({
  groupTitle: z.string().trim().max(200).nullable().optional(),
  section: z.string().trim().max(200).nullable().optional(),
  lineDate: z.string().trim().max(60).nullable().optional(),
  description: z.string().trim().min(1, "A description is required").max(300),
  qty: z.number().min(0).max(1_000_000),
  unitPrice: z.number().min(0).max(1_000_000),
  vatRate: vatRateSchema.default(DEFAULT_VAT_RATE),
});
export type InvoiceLineInput = z.infer<typeof invoiceLineInputSchema>;

/**
 * Replace a Draft invoice's whole line set in one request (PUT the lines, like the proposal builder
 * and hotel-preferences). The admin adds/removes/edits lines client-side then saves the set; the
 * server replaces the rows wholesale (delete-then-insert) inside one tenant transaction. Only a Draft
 * is editable. Capped to keep the row count (and the one-page PDF) sane.
 */
export const saveInvoiceLinesSchema = z.object({
  lines: z.array(invoiceLineInputSchema).max(100),
  /** The factura's printed document date (`Fecha`), ISO `YYYY-MM-DD`, or null to clear. */
  issueDate: z.iso.date().nullable().optional(),
  /**
   * Payment terms in days (TRAV-2) — one of {@link INVOICE_DUE_DAYS}. The due date itself is no
   * longer editable: it is stamped at issue time as `issue date + dueDays`. Omitted leaves the
   * stored terms as-is.
   */
  dueDays: z
    .number()
    .int()
    .refine(isInvoiceDueDays, { message: "Payment terms must be 0, 7, 14, 30, 60 or 90 days" })
    .optional(),
});
export type SaveInvoiceLinesInput = z.infer<typeof saveInvoiceLinesSchema>;

/**
 * Issue a Draft to the club (TRAV-2). Optional email delivery overrides: `recipients` are the
 * addresses the invoice-issued email goes to (the web pre-fills the club's accounting email and
 * splits a comma-separated entry); empty/omitted falls back to the club's accounting email
 * server-side. `message` is an optional personal note rendered into the email body. Neither affects
 * the in-app notification fan-out.
 */
export const issueInvoiceSchema = z
  .object({
    recipients: z.array(z.email("Enter a valid email").max(200)).max(20).optional(),
    message: z.string().trim().max(2000).optional(),
  })
  // A bare POST (no body) is still a valid issue — everything defaults server-side.
  .default({});
export type IssueInvoiceInput = z.infer<typeof issueInvoiceSchema>;

// ── Invoice settings (TW-3527) — the back-office org-wide issuer + numbering config ──
//
// The factura's *issuer* block is Travel Connection itself: a single org-level record edited in Back
// Office Settings → Invoicing (distinct from each club's recipient *billing details*). Saved in two
// sections (issuer details + numbering), matching the design's two Save buttons. Required-to-save for
// the issuer is company + tax number + a valid issuing email (the design's asterisks); the address,
// phone, payment terms and the multi-line payment instructions (IBAN/SWIFT etc.) are optional.

/** The issuer (TC) identity printed on every factura — saved as one section. */
export const invoiceIssuerSchema = z.object({
  companyName: z.string().trim().min(1, "Company name is required").max(200),
  taxNo: z
    .string()
    .trim()
    .min(1, "Tax number is required")
    .max(40)
    .transform((s) => s.toUpperCase()),
  email: z.email("A valid email is required").max(200),
  phone: z.string().trim().max(60).optional().default(""),
  address: z.string().trim().max(400).optional().default(""),
  paymentTerms: z.string().trim().max(300).optional().default(""),
  /** Free-text, multi-line bank / payment instructions (IBAN, SWIFT, reference note). */
  paymentInstructions: z.string().trim().max(1000).optional().default(""),
});
export type InvoiceIssuerInput = z.infer<typeof invoiceIssuerSchema>;

/** The running numbering scheme — saved as the second section. `nextNumber` ≥ 1 to save. */
export const invoiceNumberingSchema = z.object({
  prefix: z.string().trim().max(16).optional().default(""),
  suffix: z.string().trim().max(16).optional().default(""),
  nextNumber: z.number().int().min(1, "The next number must be at least 1").max(99_999_999),
  minDigits: z.number().int().min(0).max(10),
});
export type InvoiceNumberingInput = z.infer<typeof invoiceNumberingSchema>;

/**
 * Attach a TC-uploaded document to a trip (data-model §7 DOCUMENT). The client first asks for a
 * signed upload target, PUTs the bytes, then posts the object `key` back with its metadata. The key
 * namespace is validated server-side; only the key is persisted, never the bytes (mirrors the
 * passenger document-image flow, TW-3447).
 */
export const attachDocumentSchema = z.object({
  key: z.string().trim().min(1).max(500),
  filename: z.string().trim().min(1).max(255),
  contentType: z.string().trim().min(1).max(150),
  sizeBytes: z.number().int().positive().max(50_000_000, "File is too large"),
});
export type AttachDocumentInput = z.infer<typeof attachDocumentSchema>;

// ── DTOs ──────────────────────────────────────────────────────────────────────

/** An invoice as returned to the client (Decimal columns serialised to number/ISO strings). */
export interface InvoiceDto {
  id: string;
  tripId: string;
  clubId: string;
  number: string;
  status: InvoiceStatus;
  amount: number;
  currency: string;
  /** The factura's printed document date (`Fecha`), ISO `YYYY-MM-DD`, or null. */
  issueDate: string | null;
  /** The payment-due date (`Vencimiento`), ISO `YYYY-MM-DD` — stamped at issue, null on a Draft. */
  dueDate: string | null;
  /** Payment terms in days ({@link INVOICE_DUE_DAYS}); `dueDate` = issue date + this, at issue. */
  dueDays: number;
  issuedAt: string | null;
  paidAt: string | null;
  /** Whether a generated PDF is on file (the storage key itself is never sent to the client). */
  hasPdf: boolean;
  createdBy: string | null;
  createdAt: string;
}

// ── Branded record PDFs (TW-3451) ─────────────────────────────────────────────

/** The record kinds that can be rendered as a TC-branded PDF (proposal or invoice). */
export const PDF_TARGETS = ["proposal", "invoice"] as const;
export type PdfTarget = (typeof PDF_TARGETS)[number];

/** The poll id returned when a PDF generation is queued (mirrors the manifest export handshake). */
export interface PdfExportQueuedDto {
  exportId: string;
}

/**
 * Polling a queued PDF: `pending` until the worker has written it, then `ready` with a short-lived
 * signed download URL + the filename to save it as. The bytes never pass through the API/DB.
 */
export type PdfExportStatusDto =
  | { status: "pending" }
  | { status: "ready"; downloadUrl: string; filename: string; expiresAt: string };

/**
 * An invoice in the club-wide records list (TW-3452) — an {@link InvoiceDto} plus the trip context
 * needed to render + link the row. A club only ever receives Issued/Paid invoices here (the invoice
 * RLS Draft-hiding predicate); `clubName` is for the back-office cross-club view.
 */
export interface ClubInvoiceDto extends InvoiceDto {
  tripReference: string;
  tripName: string | null;
  clubName: string | null;
}

/** A factura line item as returned to the client (TW-3527) — the stored source fields; the ex/IVA/inc
 *  totals are derived via {@link invoiceLineMath}, never persisted. */
export interface InvoiceLineItemDto {
  id: string;
  /** Display-group heading row (e.g. the trip/event), or null for an ungrouped line. */
  groupTitle: string | null;
  /** Labelled subsection within a group, or null. */
  section: string | null;
  /** Free-text date / date range (never parsed), or null. */
  lineDate: string | null;
  description: string;
  qty: number;
  /** Unit price, ex-VAT. */
  unitPrice: number;
  vatRate: VatRate;
  sortOrder: number;
}

/**
 * The back-office org-wide invoicing config as returned to the client (TW-3527): the issuer block the
 * factura prints + the numbering scheme. A single record across the whole back office; never
 * club-scoped. The numbering preview is derived client-side via {@link formatInvoiceNumber}.
 */
export interface InvoiceSettingsDto {
  companyName: string;
  taxNo: string;
  email: string;
  phone: string;
  address: string;
  paymentTerms: string;
  paymentInstructions: string;
  numbering: InvoiceNumbering;
}

/** The issuer (TC) block printed on a factura — live settings for a Draft, the frozen snapshot once issued. */
export interface InvoiceIssuerBlock {
  companyName: string;
  taxNo: string;
  email: string;
  phone: string;
  address: string;
  paymentTerms: string;
  paymentInstructions: string;
}

/** The client (recipient) block — the club's active billing for a Draft, the referenced version once issued. */
export interface InvoiceClientBlock {
  companyName: string;
  taxId: string;
  street: string | null;
  postalCode: string | null;
  city: string | null;
  province: string | null;
}

/**
 * A single invoice with everything the factura page + PDF need (TW-3527): the line items, the issuer
 * block, the client billing block and the trip meta the strip shows. The issuer/client are rendered
 * **live** while the invoice is a Draft (the current settings + the club's active billing version) and
 * from the **frozen snapshot** once issued (so an issued factura never drifts). Either is null when not
 * yet configured (settings unset / the club has no billing details — the UI blocks issue until both
 * exist). The per-line ex/IVA/inc and the VAT summary are derived client-side via the pure helpers.
 */
export interface InvoiceDetailDto extends InvoiceDto {
  lineItems: InvoiceLineItemDto[];
  issuer: InvoiceIssuerBlock | null;
  client: InvoiceClientBlock | null;
  /** The club's accounting email (invoice delivery address) — pre-fills the issue modal, or null. */
  accountingEmail: string | null;
  /** Trip context for the meta strip. */
  tripReference: string;
  tripName: string | null;
  /** Travel window (ISO dates) for the "Reservation dates" cell, or null. */
  travelStartDate: string | null;
  travelEndDate: string | null;
  /** The sport/event for the "Reference" cell, or null. */
  sport: string | null;
}

/**
 * The formatted number the NEXT issued invoice will receive (TRAV-2) — an informational preview
 * shown beside the Issue button. Null when invoicing settings are not configured yet. The preview
 * is best-effort: actual allocation still happens atomically at issue time, so a concurrent issue
 * (or an externally-used number) can shift it.
 */
export interface NextInvoiceNumberDto {
  number: string | null;
}

/** A trip document as returned to the client. The private storage key is never exposed. */
export interface DocumentDto {
  id: string;
  tripId: string | null;
  clubId: string;
  kind: DocumentKind;
  filename: string;
  contentType: string;
  sizeBytes: number;
  uploadedBy: string | null;
  /** The uploader's display name (used for a club uploader's chip). Null if unknown/deleted. */
  uploaderName: string | null;
  /** Whether the uploader is back office — clubs render TC uploads as "Travel Connection". */
  uploaderIsBackOffice: boolean;
  createdAt: string;
}
